import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  Button,
  Collapse,
  IconButton,
  Tooltip,
  Stack,
  Card,
  CardContent,
  Divider,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Checkbox,
  TableSortLabel,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PrintIcon from '@mui/icons-material/Print';
import TableChartIcon from '@mui/icons-material/TableChart';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import DownloadIcon from '@mui/icons-material/Download';
import Barcode from 'react-barcode';
import { apiRequest } from '../utils/api';
import { printBarcodes, printBarcodeTable } from '../utils/printUtils';

// Format decimal inches as fractional inches, e.g. 63.25 => 63-1/4"
const formatInches = (value: number): string => {
  const whole = Math.floor(value);
  const fracOf16 = Math.round((value - whole) * 16);
  const fractionMap: Record<number, string> = {
    1: '1/16', 2: '1/8', 3: '3/16', 4: '1/4', 5: '5/16',
    6: '3/8', 7: '7/16', 8: '1/2', 9: '9/16', 10: '5/8',
    11: '11/16', 12: '3/4', 13: '13/16', 14: '7/8', 15: '15/16',
  };
  const fracStr = fractionMap[fracOf16] || '';
  if (!fracStr) return `${whole}"`;
  return whole > 0 ? `${whole}-${fracStr}"` : `${fracStr}"`;
};

// Parse "W x H" size string and format with inch units and fractions
const formatSizeString = (size: string | null): string => {
  if (!size) return '-';
  const match = size.match(/^([\d.]+)"?\s*x\s*([\d.]+)"?$/);
  if (!match) return size;
  return `${formatInches(parseFloat(match[1]))} x ${formatInches(parseFloat(match[2]))}`;
};

// Helper function to format time ago
const formatTimeAgo = (updatedAt: string | null): string => {
  if (!updatedAt) return '-';
  
  try {
    const updated = new Date(updatedAt);
    const now = new Date();
    const diffMs = now.getTime() - updated.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);

    if (diffSeconds < 60) {
      return 'Just now';
    } else if (diffMinutes < 60) {
      return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    } else if (diffWeeks < 4) {
      return `${diffWeeks} week${diffWeeks > 1 ? 's' : ''} ago`;
    } else if (diffMonths < 12) {
      return `${diffMonths} month${diffMonths > 1 ? 's' : ''} ago`;
    } else {
      return `${diffYears} year${diffYears > 1 ? 's' : ''} ago`;
    }
  } catch (error) {
    return '-';
  }
};

interface MaterialStatusDetail {
  id: number;
  product_id: number;
  job_id: number;
  job_identifier: string;
  detail_name: string;
  product_name: string;
  room: string | null;
  job_address: string | null;
  product_type_name: string | null;
  size: string | null;
  width: number | null;
  height: number | null;
  barcode: string | null;
  type_of_work: string | null;
  updated_at: string | null;
}

interface MaterialStatusGroup {
  material_status: string;
  amount: number;
  details: MaterialStatusDetail[];
}

interface JobStatusDetail {
  id: number;
  job_id: number;
  job_identifier: string;
  job_number: string;
  job_address: string;
  customer_name: string | null;
  apartment: string | null;
  updated_at: string | null;
}

interface JobStatusGroup {
  job_status: string;
  amount: number;
  details: JobStatusDetail[];
}

interface ScheduleStatusDetail {
  id: number;
  job_id: number;
  job_identifier: string;
  task_title: string;
  room_name: string | null;
  task_type: string | null;
  date_range: string;
  updated_at: string | null;
}

interface ScheduleStatusGroup {
  schedule_status: string;
  amount: number;
  details: ScheduleStatusDetail[];
}

export default function MaterialStatusDashboard() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [materialStatusGroups, setMaterialStatusGroups] = useState<MaterialStatusGroup[]>([]);
  const [jobStatusGroups, setJobStatusGroups] = useState<JobStatusGroup[]>([]);
  const [scheduleStatusGroups, setScheduleStatusGroups] = useState<ScheduleStatusGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [expandedMaterialStatuses, setExpandedMaterialStatuses] = useState<Set<string>>(new Set());
  const [expandedJobStatuses, setExpandedJobStatuses] = useState<Set<string>>(new Set());
  const [expandedScheduleStatuses, setExpandedScheduleStatuses] = useState<Set<string>>(new Set());
  const [detailStatusOptions, setDetailStatusOptions] = useState<string[]>([]);
  const [changeAllStatusDialogOpen, setChangeAllStatusDialogOpen] = useState(false);
  const [changeAllStatusGroup, setChangeAllStatusGroup] = useState<MaterialStatusGroup | null>(null);
  const [changeAllStatusValue, setChangeAllStatusValue] = useState('');
  const [changeAllStatusSaving, setChangeAllStatusSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({ open: false, message: '' });
  const [selectedMaterialIds, setSelectedMaterialIds] = useState<Set<number>>(new Set());
  const [materialSortColumn, setMaterialSortColumn] = useState<string>('');
  const [materialSortDirection, setMaterialSortDirection] = useState<'asc' | 'desc'>('asc');
  const [jobSortColumn, setJobSortColumn] = useState<string>('');
  const [jobSortDirection, setJobSortDirection] = useState<'asc' | 'desc'>('asc');
  const [scheduleSortColumn, setScheduleSortColumn] = useState<string>('');
  const [scheduleSortDirection, setScheduleSortDirection] = useState<'asc' | 'desc'>('asc');

  const handleMaterialSort = (column: string) => {
    if (materialSortColumn === column) {
      setMaterialSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setMaterialSortColumn(column);
      setMaterialSortDirection('asc');
    }
  };

  const sortMaterialDetails = (details: MaterialStatusDetail[]): MaterialStatusDetail[] => {
    if (!materialSortColumn) return details;
    return [...details].sort((a, b) => {
      let aVal: string | null = null;
      let bVal: string | null = null;
      switch (materialSortColumn) {
        case 'job':     aVal = a.job_identifier; bVal = b.job_identifier; break;
        case 'product': aVal = a.product_name;   bVal = b.product_name;   break;
        case 'room':    aVal = a.room;            bVal = b.room;           break;
        case 'size':    aVal = a.size;            bVal = b.size;           break;
        case 'barcode': aVal = a.barcode;         bVal = b.barcode;        break;
        case 'updated': aVal = a.updated_at;      bVal = b.updated_at;     break;
      }
      if (aVal === null && bVal === null) return 0;
      if (aVal === null) return 1;
      if (bVal === null) return -1;
      const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      return materialSortDirection === 'asc' ? cmp : -cmp;
    });
  };

  const handleJobSort = (column: string) => {
    if (jobSortColumn === column) {
      setJobSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setJobSortColumn(column);
      setJobSortDirection('asc');
    }
  };

  const sortJobDetails = (details: JobStatusDetail[]): JobStatusDetail[] => {
    if (!jobSortColumn) return details;
    return [...details].sort((a, b) => {
      let aVal: string | null = null;
      let bVal: string | null = null;
      switch (jobSortColumn) {
        case 'job':       aVal = a.job_identifier; bVal = b.job_identifier; break;
        case 'job_name':  aVal = a.job_number;     bVal = b.job_number;     break;
        case 'address':   aVal = a.job_address;    bVal = b.job_address;    break;
        case 'customer':  aVal = a.customer_name;  bVal = b.customer_name;  break;
        case 'apartment': aVal = a.apartment;      bVal = b.apartment;      break;
        case 'updated':   aVal = a.updated_at;     bVal = b.updated_at;     break;
      }
      if (aVal === null && bVal === null) return 0;
      if (aVal === null) return 1;
      if (bVal === null) return -1;
      const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      return jobSortDirection === 'asc' ? cmp : -cmp;
    });
  };

  const handleScheduleSort = (column: string) => {
    if (scheduleSortColumn === column) {
      setScheduleSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setScheduleSortColumn(column);
      setScheduleSortDirection('asc');
    }
  };

  const sortScheduleDetails = (details: ScheduleStatusDetail[]): ScheduleStatusDetail[] => {
    if (!scheduleSortColumn) return details;
    return [...details].sort((a, b) => {
      let aVal: string | null = null;
      let bVal: string | null = null;
      switch (scheduleSortColumn) {
        case 'job':        aVal = a.job_identifier; bVal = b.job_identifier; break;
        case 'task_title': aVal = a.task_title;     bVal = b.task_title;     break;
        case 'room':       aVal = a.room_name;      bVal = b.room_name;      break;
        case 'task_type':  aVal = a.task_type;      bVal = b.task_type;      break;
        case 'date_range': aVal = a.date_range;     bVal = b.date_range;     break;
        case 'updated':    aVal = a.updated_at;     bVal = b.updated_at;     break;
      }
      if (aVal === null && bVal === null) return 0;
      if (aVal === null) return 1;
      if (bVal === null) return -1;
      const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      return scheduleSortDirection === 'asc' ? cmp : -cmp;
    });
  };

  const loadDetailStatusOptions = async () => {
    try {
      const response = await apiRequest('/product-detail-status', { method: 'GET' });
      const data = response.data?.data || response.data || [];
      const activeStatuses = (Array.isArray(data) ? data : [])
        .filter((s: any) => s.isActive || s.is_active)
        .map((s: any) => s.statusName || s.status_name);
      const options = ['None', ...activeStatuses.filter((s: string) => s !== 'None')];
      setDetailStatusOptions(options);
    } catch (err) {
      console.error('Error loading detail status options:', err);
    }
  };

  const handleOpenChangeAllStatus = (group: MaterialStatusGroup, e: React.MouseEvent) => {
    e.stopPropagation();
    setChangeAllStatusGroup(group);
    setChangeAllStatusValue('');
    setChangeAllStatusDialogOpen(true);
  };

  const handleCloseChangeAllStatus = () => {
    setChangeAllStatusDialogOpen(false);
    setChangeAllStatusGroup(null);
    setChangeAllStatusValue('');
  };

  const handleChangeAllStatus = async () => {
    if (!changeAllStatusGroup || changeAllStatusGroup.details.length === 0) {
      setSnackbar({ open: true, message: 'No product details found' });
      return;
    }

    let statusToSave: string | null = null;
    if (changeAllStatusValue && changeAllStatusValue.trim() !== '' && changeAllStatusValue !== 'None') {
      const trimmedStatus = changeAllStatusValue.trim();
      if (!detailStatusOptions.includes(trimmedStatus)) {
        setSnackbar({ open: true, message: `Status "${trimmedStatus}" does not exist.` });
        return;
      }
      statusToSave = trimmedStatus;
    }

    setChangeAllStatusSaving(true);
    try {
      const updatePromises = changeAllStatusGroup.details.map((detail) =>
        apiRequest(`/products/${detail.product_id}/details/${detail.id}`, {
          method: 'PUT',
          body: JSON.stringify({ status: statusToSave }),
        })
      );
      await Promise.all(updatePromises);
      setSnackbar({ open: true, message: `Status updated for all ${changeAllStatusGroup.details.length} product details` });
      handleCloseChangeAllStatus();
      loadAllStatusGroups();
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message || 'Failed to update statuses' });
    } finally {
      setChangeAllStatusSaving(false);
    }
  };

  const loadMaterialStatusGroups = async () => {
    try {
      const response = await apiRequest('/jobs/material-status-dashboard', { method: 'GET' });
      const data = response.data?.data || response.data || [];
      setMaterialStatusGroups(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error('Error loading material status dashboard:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load material status dashboard';
      setError(errorMessage);
      setMaterialStatusGroups([]);
    }
  };

  const loadJobStatusGroups = async () => {
    try {
      const response = await apiRequest('/jobs/job-status-dashboard', { method: 'GET' });
      const data = response.data?.data || response.data || [];
      setJobStatusGroups(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error('Error loading job status dashboard:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load job status dashboard';
      setError(errorMessage);
      setJobStatusGroups([]);
    }
  };

  const loadScheduleStatusGroups = async () => {
    try {
      const response = await apiRequest('/jobs/schedule-status-dashboard', { method: 'GET' });
      const data = response.data?.data || response.data || [];
      setScheduleStatusGroups(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error('Error loading schedule status dashboard:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load schedule status dashboard';
      setError(errorMessage);
      setScheduleStatusGroups([]);
    }
  };

  const loadAllStatusGroups = async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([
        loadMaterialStatusGroups(),
        loadJobStatusGroups(),
        loadScheduleStatusGroups(),
      ]);
      setLastRefresh(new Date());
    } catch (err: any) {
      console.error('Error loading dashboards:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpandMaterial = (status: string) => {
    const newExpanded = new Set(expandedMaterialStatuses);
    if (newExpanded.has(status)) {
      newExpanded.delete(status);
    } else {
      newExpanded.add(status);
    }
    setExpandedMaterialStatuses(newExpanded);
  };

  const toggleExpandJob = (status: string) => {
    const newExpanded = new Set(expandedJobStatuses);
    if (newExpanded.has(status)) {
      newExpanded.delete(status);
    } else {
      newExpanded.add(status);
    }
    setExpandedJobStatuses(newExpanded);
  };

  const toggleExpandSchedule = (status: string) => {
    const newExpanded = new Set(expandedScheduleStatuses);
    if (newExpanded.has(status)) {
      newExpanded.delete(status);
    } else {
      newExpanded.add(status);
    }
    setExpandedScheduleStatuses(newExpanded);
  };

  const toggleMaterialSelection = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const newSelected = new Set(selectedMaterialIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedMaterialIds(newSelected);
  };

  const toggleSelectAllInGroup = (group: MaterialStatusGroup, e: React.MouseEvent) => {
    e.stopPropagation();
    const groupIds = group.details.map((d) => d.id);
    const allSelected = groupIds.every((id) => selectedMaterialIds.has(id));
    const newSelected = new Set(selectedMaterialIds);
    if (allSelected) {
      groupIds.forEach((id) => newSelected.delete(id));
    } else {
      groupIds.forEach((id) => newSelected.add(id));
    }
    setSelectedMaterialIds(newSelected);
  };

  const handlePrintBarcodes = (group: MaterialStatusGroup) => {
    const selectedInGroup = group.details.filter((d) => selectedMaterialIds.has(d.id));
    const detailsToPrint = selectedInGroup.length > 0 ? selectedInGroup : group.details;
    const items = detailsToPrint
      .filter((detail) => detail.barcode)
      .map((item) => ({
        barcode: item.barcode!,
        name: item.detail_name || item.product_name || 'Product',
        productName: item.detail_name ? item.product_name : undefined,
        room: item.room,
        address: item.job_address,
        productTypeName: item.product_type_name,
        width: item.width,
        height: item.height,
      }));

    printBarcodes(items, {
      title: `Print Barcodes - Status: ${group.material_status || 'All'}`,
      barcodeWidth: 4,
      barcodeHeight: 200,
      displayValue: false,
    });
  };

  const handlePrintTable = (group: MaterialStatusGroup) => {
    const statusName = group.material_status || 'All';
    const selectedInGroup = group.details.filter((d) => selectedMaterialIds.has(d.id));
    const detailsToPrint = selectedInGroup.length > 0 ? selectedInGroup : group.details;
    printBarcodeTable(
      detailsToPrint.map((item) => ({
        jobName: item.job_identifier,
        productName: item.product_name,
        room: item.room,
        size: item.size,
        barcode: item.barcode,
        typeOfWork: item.type_of_work,
      })),
      { title: `Material Status: ${statusName}` }
    );
  };

  const handleExportGroupCSV = (group: MaterialStatusGroup) => {
    const rows: string[][] = [['Product Detail', 'Material', 'Product Type', 'Size', 'Type of Work']];
    group.details.forEach((detail) => {
      rows.push([
        detail.detail_name || '',
        detail.product_name || '',
        detail.product_type_name || '',
        formatSizeString(detail.size),
        detail.type_of_work || '',
      ]);
    });
    const csv = rows.map((row) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `material-status-${group.material_status || 'unknown'}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    loadAllStatusGroups();
    loadDetailStatusOptions();
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      loadAllStatusGroups();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="overline" color="text.secondary">
            Dashboard
          </Typography>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            Dashboard (Active Jobs)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Last refreshed: {lastRefresh.toLocaleTimeString()}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={loadAllStatusGroups}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && materialStatusGroups.length === 0 && jobStatusGroups.length === 0 && scheduleStatusGroups.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Job Status Table */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Job Status
            </Typography>
            {jobStatusGroups.length === 0 ? (
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">No job status data found</Typography>
              </Paper>
            ) : isMobile ? (
              <Stack spacing={1.5}>
                {jobStatusGroups.map((group) => {
                  const isExpanded = expandedJobStatuses.has(group.job_status);
                  return (
                    <Card key={group.job_status} variant="outlined">
                      <CardContent
                        sx={{ py: 1.5, '&:last-child': { pb: 1.5 }, cursor: 'pointer' }}
                        onClick={() => toggleExpandJob(group.job_status)}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <IconButton size="small" sx={{ p: 0.5 }}>
                            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                          <Chip label={group.job_status} size="small" variant="outlined" sx={{ fontWeight: 500 }} />
                          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                            {group.amount} job{group.amount !== 1 ? 's' : ''}
                          </Typography>
                        </Box>
                      </CardContent>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Divider />
                        <Box sx={{ p: 1.5 }}>
                          <Stack spacing={1}>
                            {group.details.map((detail) => (
                              <Card
                                key={detail.id}
                                variant="outlined"
                                onClick={() => navigate(`/jobs/${detail.job_id}`)}
                                sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                              >
                                <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                                  <Typography variant="body2" fontWeight="bold">{detail.job_identifier}</Typography>
                                  {detail.job_number && <Typography variant="caption" display="block">{detail.job_number}</Typography>}
                                  {detail.job_address && <Typography variant="caption" color="text.secondary" display="block">{detail.job_address}</Typography>}
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5, alignItems: 'center' }}>
                                    {detail.customer_name && <Typography variant="caption">{detail.customer_name}</Typography>}
                                    {detail.apartment && <Typography variant="caption">Apt: {detail.apartment}</Typography>}
                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>{formatTimeAgo(detail.updated_at)}</Typography>
                                  </Box>
                                </CardContent>
                              </Card>
                            ))}
                          </Stack>
                        </Box>
                      </Collapse>
                    </Card>
                  );
                })}
              </Stack>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell width="10%"><strong>Job Status</strong></TableCell>
                      <TableCell width="10%"><strong>Amount</strong></TableCell>
                      <TableCell width="80%"><strong>Detail</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {jobStatusGroups.map((group) => {
                      const isExpanded = expandedJobStatuses.has(group.job_status);
                      return (
                        <React.Fragment key={group.job_status}>
                          <TableRow hover>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <IconButton
                                  size="small"
                                  onClick={(e) => { e.stopPropagation(); toggleExpandJob(group.job_status); }}
                                  sx={{ p: 0.5 }}
                                >
                                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                                <Chip label={group.job_status} size="small" variant="outlined" sx={{ fontWeight: 500 }} />
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>{group.amount}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {group.details.length} job{group.details.length > 1 ? 's' : ''} - Click to expand
                              </Typography>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell colSpan={3} sx={{ py: 0, border: 0 }}>
                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Box sx={{ p: 2, bgcolor: 'background.default' }}>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        {([
                                          { key: 'job',       label: 'Job' },
                                          { key: 'job_name',  label: 'Job Name' },
                                          { key: 'address',   label: 'Address' },
                                          { key: 'customer',  label: 'Customer' },
                                          { key: 'apartment', label: 'Apartment' },
                                          { key: 'updated',   label: 'Updated' },
                                        ] as const).map(({ key, label }) => (
                                          <TableCell key={key}>
                                            <TableSortLabel
                                              active={jobSortColumn === key}
                                              direction={jobSortColumn === key ? jobSortDirection : 'asc'}
                                              onClick={() => handleJobSort(key)}
                                            >
                                              <strong>{label}</strong>
                                            </TableSortLabel>
                                          </TableCell>
                                        ))}
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {sortJobDetails(group.details).map((detail) => (
                                        <TableRow
                                          key={detail.id}
                                          onClick={() => navigate(`/jobs/${detail.job_id}`)}
                                          sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                                        >
                                          <TableCell><Typography variant="body2">{detail.job_identifier}</Typography></TableCell>
                                          <TableCell><Typography variant="body2">{detail.job_number || '-'}</Typography></TableCell>
                                          <TableCell><Typography variant="body2">{detail.job_address || '-'}</Typography></TableCell>
                                          <TableCell><Typography variant="body2">{detail.customer_name || '-'}</Typography></TableCell>
                                          <TableCell><Typography variant="body2">{detail.apartment || '-'}</Typography></TableCell>
                                          <TableCell><Typography variant="body2" color="text.secondary">{formatTimeAgo(detail.updated_at)}</Typography></TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>

          {/* Schedule Status Table */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Schedule Status
            </Typography>
            {scheduleStatusGroups.length === 0 ? (
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">No schedule status data found</Typography>
              </Paper>
            ) : isMobile ? (
              <Stack spacing={1.5}>
                {scheduleStatusGroups.map((group) => {
                  const isExpanded = expandedScheduleStatuses.has(group.schedule_status);
                  return (
                    <Card key={group.schedule_status} variant="outlined">
                      <CardContent
                        sx={{ py: 1.5, '&:last-child': { pb: 1.5 }, cursor: 'pointer' }}
                        onClick={() => toggleExpandSchedule(group.schedule_status)}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <IconButton size="small" sx={{ p: 0.5 }}>
                            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                          <Chip label={group.schedule_status} size="small" variant="outlined" sx={{ fontWeight: 500 }} />
                          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                            {group.amount} task{group.amount !== 1 ? 's' : ''}
                          </Typography>
                        </Box>
                      </CardContent>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Divider />
                        <Box sx={{ p: 1.5 }}>
                          <Stack spacing={1}>
                            {group.details.map((detail) => (
                              <Card
                                key={detail.id}
                                variant="outlined"
                                onClick={() => navigate(`/jobs/${detail.job_id}#product`)}
                                sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                              >
                                <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                                  <Typography variant="body2" fontWeight="bold">{detail.task_title || '-'}</Typography>
                                  <Typography variant="caption" color="text.secondary" display="block">{detail.job_identifier}</Typography>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5, alignItems: 'center' }}>
                                    {detail.room_name && <Typography variant="caption">Room: {detail.room_name}</Typography>}
                                    {detail.task_type && <Typography variant="caption">{detail.task_type}</Typography>}
                                    {detail.date_range && <Typography variant="caption" color="text.secondary">{detail.date_range}</Typography>}
                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>{formatTimeAgo(detail.updated_at)}</Typography>
                                  </Box>
                                </CardContent>
                              </Card>
                            ))}
                          </Stack>
                        </Box>
                      </Collapse>
                    </Card>
                  );
                })}
              </Stack>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell width="10%"><strong>Schedule Status</strong></TableCell>
                      <TableCell width="10%"><strong>Amount</strong></TableCell>
                      <TableCell width="80%"><strong>Detail</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {scheduleStatusGroups.map((group) => {
                      const isExpanded = expandedScheduleStatuses.has(group.schedule_status);
                      return (
                        <React.Fragment key={group.schedule_status}>
                          <TableRow hover>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <IconButton
                                  size="small"
                                  onClick={(e) => { e.stopPropagation(); toggleExpandSchedule(group.schedule_status); }}
                                  sx={{ p: 0.5 }}
                                >
                                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                                <Chip label={group.schedule_status} size="small" variant="outlined" sx={{ fontWeight: 500 }} />
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>{group.amount}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {group.details.length} task{group.details.length > 1 ? 's' : ''} - Click to expand
                              </Typography>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell colSpan={3} sx={{ py: 0, border: 0 }}>
                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Box sx={{ p: 2, bgcolor: 'background.default' }}>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        {([
                                          { key: 'job',        label: 'Job' },
                                          { key: 'task_title', label: 'Task Title' },
                                          { key: 'room',       label: 'Room' },
                                          { key: 'task_type',  label: 'Task Type' },
                                          { key: 'date_range', label: 'Date Range' },
                                          { key: 'updated',    label: 'Updated' },
                                        ] as const).map(({ key, label }) => (
                                          <TableCell key={key}>
                                            <TableSortLabel
                                              active={scheduleSortColumn === key}
                                              direction={scheduleSortColumn === key ? scheduleSortDirection : 'asc'}
                                              onClick={() => handleScheduleSort(key)}
                                            >
                                              <strong>{label}</strong>
                                            </TableSortLabel>
                                          </TableCell>
                                        ))}
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {sortScheduleDetails(group.details).map((detail) => (
                                        <TableRow
                                          key={detail.id}
                                          onClick={() => navigate(`/jobs/${detail.job_id}#product`)}
                                          sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                                        >
                                          <TableCell><Typography variant="body2">{detail.job_identifier}</Typography></TableCell>
                                          <TableCell><Typography variant="body2">{detail.task_title || '-'}</Typography></TableCell>
                                          <TableCell><Typography variant="body2">{detail.room_name || '-'}</Typography></TableCell>
                                          <TableCell><Typography variant="body2">{detail.task_type || '-'}</Typography></TableCell>
                                          <TableCell><Typography variant="body2">{detail.date_range || '-'}</Typography></TableCell>
                                          <TableCell><Typography variant="body2" color="text.secondary">{formatTimeAgo(detail.updated_at)}</Typography></TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>

          {/* Material Status Table */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Material Status
            </Typography>
            {materialStatusGroups.length === 0 ? (
              <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">No material status data found</Typography>
              </Paper>
            ) : isMobile ? (
              <Stack spacing={1.5}>
                {materialStatusGroups.map((group) => {
                  const isExpanded = expandedMaterialStatuses.has(group.material_status);
                  return (
                    <Card key={group.material_status} variant="outlined">
                      <CardContent
                        sx={{ py: 1.5, '&:last-child': { pb: 1.5 }, cursor: 'pointer' }}
                        onClick={() => toggleExpandMaterial(group.material_status)}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <IconButton size="small" sx={{ p: 0.5 }}>
                            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                          <Chip label={group.material_status} size="small" variant="outlined" sx={{ fontWeight: 500 }} />
                          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                            {group.amount} item{group.amount !== 1 ? 's' : ''}
                          </Typography>
                          <Tooltip title="Change All Status">
                            <IconButton
                              size="small"
                              onClick={(e) => handleOpenChangeAllStatus(group, e)}
                            >
                              <SwapHorizIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Print Table">
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); handlePrintTable(group); }}
                            >
                              <TableChartIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {group.details.some(d => d.barcode) && (
                            <Tooltip title="Print Barcodes">
                              <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); handlePrintBarcodes(group); }}
                              >
                                <PrintIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Export CSV">
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); handleExportGroupCSV(group); }}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </CardContent>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Divider />
                        <Box sx={{ p: 1.5 }}>
                          <Stack spacing={1}>
                            {group.details.map((detail) => (
                              <Card
                                key={detail.id}
                                variant="outlined"
                                onClick={() => navigate(`/jobs/${detail.job_id}#product`)}
                                sx={{
                                  cursor: 'pointer',
                                  '&:hover': { bgcolor: 'action.hover' },
                                  ...(selectedMaterialIds.has(detail.id) && { outline: '2px solid', outlineColor: 'primary.main' }),
                                }}
                              >
                                <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                                    <Checkbox
                                      size="small"
                                      checked={selectedMaterialIds.has(detail.id)}
                                      onClick={(e) => toggleMaterialSelection(detail.id, e)}
                                      sx={{ mt: -0.5, ml: -0.5 }}
                                    />
                                    <Box sx={{ flex: 1 }}>
                                      <Typography variant="body2" fontWeight="bold">{detail.product_name || '-'}</Typography>
                                      <Typography variant="caption" color="text.secondary" display="block">{detail.job_identifier}</Typography>
                                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5, alignItems: 'center' }}>
                                        {detail.room && <Typography variant="caption">Room: {detail.room}</Typography>}
                                        {detail.size && <Typography variant="caption">Size: {formatSizeString(detail.size)}</Typography>}
                                        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>{formatTimeAgo(detail.updated_at)}</Typography>
                                      </Box>
                                      {detail.barcode && (
                                        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center', '& svg': { maxWidth: '140px', height: 'auto' } }}>
                                          <Barcode value={detail.barcode} format="CODE128" width={1} height={35} displayValue={false} fontSize={10} />
                                        </Box>
                                      )}
                                    </Box>
                                  </Box>
                                </CardContent>
                              </Card>
                            ))}
                          </Stack>
                        </Box>
                      </Collapse>
                    </Card>
                  );
                })}
              </Stack>
            ) : (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell width="10%"><strong>Material Status</strong></TableCell>
                      <TableCell width="10%"><strong>Amount</strong></TableCell>
                      <TableCell width="80%"><strong>Detail</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {materialStatusGroups.map((group) => {
                      const isExpanded = expandedMaterialStatuses.has(group.material_status);
                      return (
                        <React.Fragment key={group.material_status}>
                          <TableRow hover>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <IconButton
                                  size="small"
                                  onClick={(e) => { e.stopPropagation(); toggleExpandMaterial(group.material_status); }}
                                  sx={{ p: 0.5 }}
                                >
                                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                                <Chip label={group.material_status} size="small" variant="outlined" sx={{ fontWeight: 500 }} />
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>{group.amount}</Typography>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                  {group.details.length > 0
                                    ? `${group.details.length} item${group.details.length > 1 ? 's' : ''} - Click to expand`
                                    : 'No details'}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
                                  <Tooltip title="Change All Status">
                                    <IconButton
                                      size="small"
                                      onClick={(e) => handleOpenChangeAllStatus(group, e)}
                                    >
                                      <SwapHorizIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Print Table">
                                    <IconButton
                                      size="small"
                                      onClick={(e) => { e.stopPropagation(); handlePrintTable(group); }}
                                    >
                                      <TableChartIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  {group.details.some(d => d.barcode) && (
                                    <Tooltip title="Print Barcodes">
                                      <IconButton
                                        size="small"
                                        onClick={(e) => { e.stopPropagation(); handlePrintBarcodes(group); }}
                                      >
                                        <PrintIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  <Tooltip title="Export CSV">
                                    <IconButton
                                      size="small"
                                      onClick={(e) => { e.stopPropagation(); handleExportGroupCSV(group); }}
                                    >
                                      <DownloadIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </Box>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell colSpan={3} sx={{ py: 0, border: 0 }}>
                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Box sx={{ p: 2, bgcolor: 'background.default' }}>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell padding="checkbox">
                                          <Checkbox
                                            size="small"
                                            checked={group.details.length > 0 && group.details.every((d) => selectedMaterialIds.has(d.id))}
                                            indeterminate={group.details.some((d) => selectedMaterialIds.has(d.id)) && !group.details.every((d) => selectedMaterialIds.has(d.id))}
                                            onClick={(e) => toggleSelectAllInGroup(group, e)}
                                          />
                                        </TableCell>
                                        {(['job', 'product', 'room', 'size', 'barcode', 'updated'] as const).map((col) => (
                                          <TableCell key={col} align={col === 'barcode' ? 'center' : 'left'}>
                                            <TableSortLabel
                                              active={materialSortColumn === col}
                                              direction={materialSortColumn === col ? materialSortDirection : 'asc'}
                                              onClick={() => handleMaterialSort(col)}
                                            >
                                              <strong>{col.charAt(0).toUpperCase() + col.slice(1)}</strong>
                                            </TableSortLabel>
                                          </TableCell>
                                        ))}
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {sortMaterialDetails(group.details).map((detail) => (
                                        <TableRow
                                          key={detail.id}
                                          onClick={() => navigate(`/jobs/${detail.job_id}#product`)}
                                          selected={selectedMaterialIds.has(detail.id)}
                                          sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                                        >
                                          <TableCell padding="checkbox" onClick={(e) => toggleMaterialSelection(detail.id, e)}>
                                            <Checkbox size="small" checked={selectedMaterialIds.has(detail.id)} />
                                          </TableCell>
                                          <TableCell><Typography variant="body2">{detail.job_identifier}</Typography></TableCell>
                                          <TableCell><Typography variant="body2">{detail.product_name || '-'}</Typography></TableCell>
                                          <TableCell><Typography variant="body2">{detail.room || '-'}</Typography></TableCell>
                                          <TableCell><Typography variant="body2">{formatSizeString(detail.size)}</Typography></TableCell>
                                          <TableCell>
                                            {detail.barcode ? (
                                              <Box sx={{ display: 'flex', justifyContent: 'center', '& svg': { maxWidth: '150px', height: 'auto' } }}>
                                                <Barcode value={detail.barcode} format="CODE128" width={1} height={40} displayValue={false} fontSize={12} />
                                              </Box>
                                            ) : (
                                              <Typography variant="body2" color="text.secondary">-</Typography>
                                            )}
                                          </TableCell>
                                          <TableCell><Typography variant="body2" color="text.secondary">{formatTimeAgo(detail.updated_at)}</Typography></TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Box>
      )}

      {/* Change All Status Dialog */}
      <Dialog open={changeAllStatusDialogOpen} onClose={handleCloseChangeAllStatus} maxWidth="xs" fullWidth>
        <DialogTitle>
          Change All Status
          {changeAllStatusGroup && (
            <Typography variant="body2" color="text.secondary">
              {changeAllStatusGroup.amount} item{changeAllStatusGroup.amount !== 1 ? 's' : ''} in "{changeAllStatusGroup.material_status}"
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>New Status</InputLabel>
            <Select
              value={changeAllStatusValue}
              label="New Status"
              onChange={(e) => setChangeAllStatusValue(e.target.value)}
            >
              {detailStatusOptions.map((status) => (
                <MenuItem key={status} value={status}>{status}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseChangeAllStatus} disabled={changeAllStatusSaving}>Cancel</Button>
          <Button
            onClick={handleChangeAllStatus}
            variant="contained"
            disabled={!changeAllStatusValue || changeAllStatusSaving}
          >
            {changeAllStatusSaving ? <CircularProgress size={20} /> : 'Update All'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ open: false, message: '' })}
        message={snackbar.message}
      />
    </Box>
  );
}
